import React, { useState } from "react";
import { Search, MapPin, Globe, Star, ShieldCheck, Clock } from "lucide-react";
import { motion } from "framer-motion";
import HeroImage from "../../assets/images/hero-bg.png"; 

const Banner = () => {
  const [language, setLanguage] = useState("EN");

  const toggleLanguage = () => {
    setLanguage(language === "EN" ? "UR" : "EN");
  };

  const content = {
    EN: {
      headline: "Find trusted professionals for every home service.",
      highlight: "professionals",
      subhead: "Book electricians, plumbers, cleaners and technicians within minutes.",
      placeholder: "What service do you need?",
      button: "Search",
      locationPlaceholder: "Location (e.g. Lahore)"
    },
    UR: {
      headline: "ہر گھریلو سروس کے لیے قابل اعتماد پیشہ ور افراد تلاش کریں۔",
      highlight: "پیشہ",
      subhead: "منٹوں میں الیکٹریشنز، پلمبرز، کلینرز اور ٹیکنیشنز بک کریں۔",
      placeholder: "آپ کو کس سروس کی ضرورت ہے؟",
      button: "تلاش کریں",
      locationPlaceholder: "مقام (جیسے لاہور)"
    }
  };

  const t = content[language];

  // Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const floatingAnimation = {
    y: ["0px", "-10px", "0px"],
    transition: {
      duration: 7,
      ease: "easeInOut",
      repeat: Infinity,
    }
  };

  const cardFloatAnimation = (delay) => ({
    y: ["0px", "-6px", "0px"],
    transition: {
      duration: 5,
      ease: "easeInOut",
      repeat: Infinity,
      delay: delay
    }
  });

  return (
    <div className="relative min-h-[850px] flex items-center justify-center pt-24 pb-32">
      {/* 9.5/10 Hero Identity Background */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          background: `
            radial-gradient(circle at top right, rgba(59,130,246,0.12), transparent 55%),
            radial-gradient(circle at bottom left, rgba(99,102,241,0.08), transparent 60%),
            linear-gradient(180deg, #FCFDFF, #F5F9FF)
          `
        }}
      ></div>
        
      {/* Decorative Blurred Circles & Mesh (Opacity 3-8%) */}
      <div className="absolute top-[10%] left-[10%] w-[500px] h-[500px] bg-blue-500 rounded-full blur-[140px] opacity-[0.04] mix-blend-multiply z-0 pointer-events-none"></div>
      <div className="absolute bottom-[20%] right-[10%] w-[600px] h-[600px] bg-indigo-500 rounded-full blur-[160px] opacity-[0.05] mix-blend-multiply z-0 pointer-events-none"></div>
      
      {/* Abstract Texture Overlay */}
      <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] z-0 pointer-events-none"></div>

      {/* Smooth Transition Out to Categories Section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#F8FAFC] to-transparent z-10 pointer-events-none"></div>

      <div className="container mx-auto px-6 md:px-12 relative z-20 py-12 md:py-24">
        
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-12 items-center">
          
          {/* Left Column: Text & Search */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="space-y-10"
          >
            <div className="space-y-6 max-w-2xl">
              {/* Language Toggle */}
              <motion.div variants={itemVariants} className="flex items-center">
                <button 
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 px-4 py-2 bg-white/60 hover:bg-white border border-blue-200 rounded-full transition-all text-slate-700 backdrop-blur-md text-sm font-bold shadow-sm hover:shadow-md"
                >
                  <Globe className="w-4 h-4 text-brand-primary" />
                  {language === "EN" ? "Switch to Urdu (اردو)" : "Switch to English"}
                </button>
              </motion.div>
              
              <motion.h1 
                variants={itemVariants}
                className="font-bold text-slate-900 tracking-tight text-[48px] md:text-[56px] lg:text-[64px] leading-[1.1]"
                style={{ direction: language === 'UR' ? 'rtl' : 'ltr', fontFamily: language === 'UR' ? 'Jameel Noori Nastaleeq, Noto Nastaliq Urdu, sans-serif' : 'inherit' }}
              >
                {t.headline.split(' ').map((word, i) => (
                  <span 
                    key={i} 
                    className={word.includes(t.highlight) ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-500 relative' : ''}
                  >
                    {word}{' '}
                  </span>
                ))}
              </motion.h1>
              
              <motion.p 
                variants={itemVariants}
                className={`text-slate-600 ${language === 'UR' ? 'text-xl md:text-2xl' : 'text-lg md:text-[22px]'} leading-relaxed max-w-xl`}
                style={{ direction: language === 'UR' ? 'rtl' : 'ltr', fontFamily: language === 'UR' ? 'Jameel Noori Nastaleeq, Noto Nastaliq Urdu, sans-serif' : 'inherit' }}
              >
                {t.subhead}
              </motion.p>
            </div>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 pt-2 relative">
              {/* Button Ambient Glow */}
              <div className="absolute top-0 left-0 w-32 h-14 bg-blue-500 rounded-full blur-2xl opacity-30 pointer-events-none"></div>
              
              <button className="relative z-10 h-[56px] px-8 bg-gradient-to-b from-[#2563EB] to-[#3B82F6] text-white rounded-[14px] font-semibold shadow-[0_8px_20px_rgba(37,99,235,0.25)] hover:shadow-[0_12px_30px_rgba(37,99,235,0.4)] transition-all duration-300 transform hover:-translate-y-[2px] hover:scale-[1.02]">
                Book Service
              </button>
              <button className="h-[56px] px-8 bg-white border border-slate-200 text-brand-text rounded-[14px] font-semibold shadow-sm hover:bg-[#F5F7FB] transition-all duration-300">
                Become Provider
              </button>
            </motion.div>

            {/* Search Component (Massive Floating Card) */}
            <motion.div 
              variants={itemVariants}
              className="mt-14 relative"
            >
              {/* Search Box Ambient Lighting */}
              <div className="absolute inset-0 bg-blue-100 rounded-[24px] blur-2xl opacity-50 transform translate-y-4 pointer-events-none"></div>
              
              <div className="relative bg-white p-6 rounded-[24px] shadow-[0_20px_60px_rgba(15,23,42,0.08)] flex flex-col md:flex-row gap-4 border border-white z-20 hover:shadow-[0_25px_65px_rgba(15,23,42,0.12)] hover:-translate-y-1 transition-all duration-300">
                <div className="flex-1 flex items-center gap-3 px-4 h-[60px] bg-[#F8FAFC] rounded-[16px] border border-transparent focus-within:border-brand-primary/40 focus-within:ring-4 focus-within:ring-brand-primary/10 transition-all group">
                  <Search className="w-5 h-5 text-slate-400 group-focus-within:text-brand-primary transition-colors" />
                  <input
                    type="text"
                    placeholder={t.placeholder}
                    className="w-full bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-400 font-medium text-base"
                    style={{ direction: language === 'UR' ? 'rtl' : 'ltr' }}
                  />
                </div>
                
                <div className="hidden md:block w-[1px] bg-slate-100 my-2 mx-1"></div>

                <div className="flex-1 flex items-center gap-3 px-4 h-[60px] bg-[#F8FAFC] rounded-[16px] border border-transparent focus-within:border-brand-primary/40 focus-within:ring-4 focus-within:ring-brand-primary/10 transition-all group">
                  <MapPin className="w-5 h-5 text-slate-400 group-focus-within:text-brand-primary transition-colors" />
                  <input
                    type="text"
                    placeholder={t.locationPlaceholder}
                    className="w-full bg-transparent border-none outline-none text-slate-900 placeholder:text-slate-400 font-medium text-base"
                    style={{ direction: language === 'UR' ? 'rtl' : 'ltr' }}
                  />
                </div>
                <button className="h-[60px] px-8 bg-brand-primary hover:bg-blue-700 text-white rounded-[16px] font-bold transition-all duration-300 shadow-md w-full md:w-auto shrink-0 transform hover:scale-[1.02]">
                  {t.button}
                </button>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column: Floating Illustration & Metrics */}
          <div className="relative h-[650px] hidden lg:block">
            {/* Hero Ambient Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[radial-gradient(circle,_rgba(59,130,246,0.18),_transparent_70%)] blur-[90px] rounded-full pointer-events-none z-0"></div>

            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
              className="absolute inset-0 rounded-[40px] overflow-hidden shadow-[0_30px_80px_rgba(15,23,42,0.06)] border border-white/80 bg-white z-10"
            >
              <motion.img
                animate={floatingAnimation}
                src={HeroImage}
                alt="Professional Services"
                className="w-full h-full object-cover object-center mix-blend-multiply opacity-85 scale-[1.02]"
              />
            </motion.div>

            {/* Floating Metric Cards with Parallax/Independent Animation */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="absolute top-16 -left-6 z-30"
            >
              <motion.div 
                animate={cardFloatAnimation(0)}
                className="bg-white/90 backdrop-blur-lg p-5 rounded-[20px] shadow-[0_15px_40px_rgba(0,0,0,0.08)] border border-white hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition-shadow duration-300"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <img key={i} className="w-10 h-10 rounded-full border-2 border-white" src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" />
                    ))}
                  </div>
                  <div className="flex text-[#F5A623]">
                    {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-4 h-4 fill-current" />)}
                  </div>
                </div>
                <p className="text-sm font-bold text-brand-text">2,500+ Verified Professionals</p>
              </motion.div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="absolute bottom-40 -right-8 z-30"
            >
              <motion.div 
                animate={cardFloatAnimation(0.4)}
                className="bg-white/90 backdrop-blur-lg p-5 rounded-[20px] shadow-[0_15px_40px_rgba(0,0,0,0.08)] border border-white flex items-center gap-4 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition-shadow duration-300"
              >
                <div className="w-12 h-12 rounded-full bg-brand-success/10 flex items-center justify-center text-brand-success">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[26px] font-bold text-brand-text leading-tight">98%</p>
                  <p className="text-sm font-semibold text-brand-muted">Customer Satisfaction</p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute bottom-12 left-8 z-30"
            >
              <motion.div 
                animate={cardFloatAnimation(0.8)}
                className="bg-white/90 backdrop-blur-lg p-5 rounded-[20px] shadow-[0_15px_40px_rgba(0,0,0,0.08)] border border-white flex items-center gap-4 hover:shadow-[0_20px_50px_rgba(0,0,0,0.12)] transition-shadow duration-300"
              >
                 <div className="w-12 h-12 rounded-full bg-brand-accent/10 flex items-center justify-center text-brand-accent">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-[22px] font-bold text-brand-text leading-tight">24/7</p>
                  <p className="text-sm font-semibold text-brand-muted">Rapid Support</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Banner;
